import { Badge, Box, Flex, Grid, GridItem, Text, useToast } from "@chakra-ui/react";
import React, { Fragment, useEffect, useState } from "react";
import { ResponsiveRadialBar } from "@nivo/radial-bar";
import { nivo_theme } from "../../../ISSB/Dashboard/components/chart-utils";
import { ResponsivePie } from "@nivo/pie";
import { getFormattedNumValue } from "../../../../utils";
import SupplierListVisual from "../supplier-engagement/components/SupplierListVisual";

const SupplierInsightsVisual = ({ currentYearData, previousYearData, filters, setFilters }) => {
  return (
    <Box height="500px" width="100%">

      <Grid templateColumns="repeat(3, 1fr)" gap={4} mb={6}>
        <GridItem>
          <ScopeRadialBar currentYearData={currentYearData} previousYearData={previousYearData} />
        </GridItem>
        <GridItem>
          <DeltaGauge data={currentYearData} />
        </GridItem>
        <GridItem>
          <SupplierListVisual filters={filters} setFilters={setFilters} showHeatMap={false} />
        </GridItem>
      </Grid>
    </Box>
  );
};

const ScopeRadialBar = ({ currentYearData, previousYearData }) => {
  // // Transform data to match the expected format for ResponsiveRadialBar
  const [groupData, setGroupData] = useState([]);

  useEffect(() => {
    if (!currentYearData) return;

    const constructedData = [
      {
        id: "Scope 1",
        data: [
          {
            x: "Scope 1",
            y: Number(currentYearData?.scope1Emissions) || 0,
            color: "#9a34f3",
          },
        ],
      },
      {
        id: "Scope 2",
        data: [
          {
            x: "Scope 2",
            y: Number(currentYearData?.scope2Emissions) || 0,
            color: "#ffd007",
          },
        ],
      },
      {
        id: "Scope 3",
        data: [
          {
            x: "Scope 3",
            y: Number(currentYearData?.scope3Emissions) || 0,
            color: "#5eb5f3",
          },
        ],
      },
      {
        id: "Total",
        data: [
          {
            x: "Total",
            y: Number(currentYearData?.totalEmissions) || 0,
            color: "#0c3136",
          },
        ],
      },
    ];
    setGroupData(constructedData);
  }, [currentYearData]);

  // Custom tooltip component
  const CustomTooltip = ({ bar }) => {
    const emissions = bar.data.y || 0;
    const previousEmissions = previousYearData?.[bar.groupId.toLowerCase().replace(" ", "") + "Emissions"] || 0; // e.g., scope1Emissions, scope2Emissions, scope3Emissions, totalEmissions
    const reduction = previousEmissions ? ((previousEmissions - emissions) / previousEmissions) * 100 : 0;
    const totalSpend = currentYearData?.totalSpend; // Convert to £m
    const intensity = emissions / (totalSpend || 1); // Avoid division by zero

    return (
      <Box bg="white" p={3} borderRadius="md" boxShadow="lg" border="1px solid" borderColor="gray.200">
        <Text fontWeight="bold" color={bar.color}>
          {bar.groupId}
        </Text>
        <Text>Emissions: {getFormattedNumValue(emissions, 2)} tCO2e</Text>
        <Text>Intensity: {getFormattedNumValue(intensity, 2)} tCO2e per £m</Text>
        <Text>Reduction: {getFormattedNumValue(reduction, 2)} %</Text>
      </Box>
    );
  };

  return (
    <Box height="500px" width="100%" p={2}>
      <ResponsiveRadialBar
        data={groupData}
        innerRadius={0.3}
        padding={0.3}
        cornerRadius={2}
        borderColor={{
          from: "color",
          modifiers: [["darker", "1"]],
        }}
        colors={(d) => d.data.color}
        enableRadialGrid={false}
        enableCircularGrid={false}
        radialAxisStart={{ tickSize: 5, tickPadding: 5, tickRotation: 0 }}
        circularAxisOuter={null}
        legends={[]}
        // onClick={(data) => handleClick(data?.groupId, data?.category)}
        theme={nivo_theme}
        tooltip={CustomTooltip}
      />
    </Box>
  );
};

const DeltaGauge = ({ data }) => {
  let delta = data?.totalTarget ? ((data?.totalEmissions - data?.totalTarget) / data?.totalTarget) * 100 : 0;

  const chartData = [
    {
      id: "Emissions",
      value: data?.totalEmissions || 0,
      color: data?.totalEmissions > data?.totalTarget ? "#ff6b6b" : "#82D909",
    },
    {
      id: "Remaining",
      value: Math.max(data?.totalTarget * 2 - data?.totalEmissions, 0) || 0,
      color: "#E5E7EB", // Gray color for remaining emissions
    },
  ];

  const TargetLine = ({ centerX, centerY, radius, innerRadius }) => {
    // Draw a line at the top-middle of the gauge (-90° within the -120°..120° span)
    const angleDeg = -90;
    const angleRad = (angleDeg * Math.PI) / 180;

    const x1 = centerX + Math.cos(angleRad) * innerRadius;
    const y1 = centerY + Math.sin(angleRad) * innerRadius;
    const x2 = centerX + Math.cos(angleRad) * radius;
    const y2 = centerY + Math.sin(angleRad) * radius;

    // Calculate label position slightly outside the outer radius
    const labelRadius = radius + 16;
    const lx = centerX + Math.cos(angleRad) * labelRadius;
    const ly = centerY + Math.sin(angleRad) * labelRadius;

    return (
      <>
        <line
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="#4B5563"
          strokeWidth={4}
          strokeLinecap="round"
          pointerEvents="none"
        />
        <text x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 12, fill: "#4B5563" }}>
          Target {getFormattedNumValue(data?.totalTarget || 0)} (tCO2e)
        </text>
      </>
    );
  };

  return (
    <Box height="500px" width="100%" position="relative" p={2}>
      <ResponsivePie
        data={chartData}
        innerRadius={0.9}
        padAngle={1}
        cornerRadius={3}
        colors={(d) => d.data.color} // e.g. stage.queued in colors.js
        startAngle={-120}
        endAngle={120}
        enableArcLabels={false}
        enableArcLinkLabels={false}
        // onClick={onClick}
        theme={nivo_theme}
        layers={["arcs", TargetLine]}
        tooltip={({ datum }) => (
          <Box bg="white" p="8px" border="1px solid #ccc" borderRadius="4px">
            <Text fontWeight="bold" color={datum.color}>
              {datum.id}
            </Text>
            <Text>Value: {getFormattedNumValue(Number(datum.value), 2)} tCO2e</Text>
          </Box>
        )}
      />
      <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" textAlign="center">
        <Text fontSize="2xl" fontWeight="bold">
          {delta?.toFixed(2)} %
        </Text>
        <Text fontSize="sm" color="gray.500">
          Delta
        </Text>
      </Box>
    </Box>
  );
};

export default SupplierInsightsVisual;
